import React from 'react';
import { AlertTriangle, CheckCircle, Info, X, Loader2 } from 'lucide-react';

// ─── Button ────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, type = 'button', style = {} }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font)',
    fontWeight: 600, borderRadius: 'var(--radius-sm)', border: 'none', cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all .2s', whiteSpace: 'nowrap', letterSpacing: '-0.01em',
    opacity: disabled || loading ? 0.65 : 1,
  };
  const sizes = {
    sm: { padding: '6px 12px', fontSize: 12 },
    md: { padding: '9px 18px', fontSize: 13.5 },
    lg: { padding: '12px 24px', fontSize: 15 },
  };
  const variants = {
    primary: { background: 'var(--primary)', color: '#fff', boxShadow: '0 2px 8px rgba(27,63,148,.3)' },
    secondary: { background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1.5px solid var(--border)' },
    danger: { background: 'var(--danger)', color: '#fff', boxShadow: '0 2px 8px rgba(239,68,68,.25)' },
    ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1.5px solid transparent' },
    success: { background: 'var(--success)', color: '#fff' },
  };
  return (
    <button type={type} disabled={disabled || loading} onClick={onClick}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      onMouseEnter={e => { if (!disabled && !loading) e.currentTarget.style.filter = 'brightness(1.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
    >
      {loading ? <Loader2 size={14} className="spin" /> : Icon ? <Icon size={size === 'sm' ? 13 : 15} /> : null}
      {children}
    </button>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────
export function Card({ children, style = {}, className = '', onClick }) {
  return (
    <div className={`fade-in ${className}`} onClick={onClick} style={{
      background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', padding: '24px',
      border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)',
      transition: 'box-shadow .2s, transform .15s',
      cursor: onClick ? 'pointer' : 'default',
      ...style,
    }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
    onMouseLeave={e => { if (onClick) { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'none'; } }}
    >
      {children}
    </div>
  );
}

// ─── StatCard ──────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon: Icon, color, subtitle, trend }) {
  return (
    <div className="fade-in" style={{
      background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', padding: '20px 24px',
      border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: color, opacity: 0.08 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={color} />
        </div>
        {trend !== undefined && (
          <span style={{ fontSize: 11, fontWeight: 600, color: trend >= 0 ? 'var(--success)' : 'var(--danger)', background: (trend >= 0 ? 'var(--success)' : 'var(--danger)') + '15', padding: '2px 8px', borderRadius: 99 }}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, letterSpacing: '-0.02em' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontWeight: 500 }}>{label}</div>
      {subtitle && <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

// ─── PageHeader ────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action, breadcrumb }) {
  return (
    <div style={{ marginBottom: 28, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div>
        {breadcrumb && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 500, letterSpacing: '.04em', textTransform: 'uppercase' }}>{breadcrumb}</div>}
        <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.03em', lineHeight: 1.2 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 4 }}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ─── Badge ─────────────────────────────────────────────────────────────────
const BADGE_COLORS = {
  active: { bg: '#dcfce7', color: '#15803d' },
  inactive: { bg: '#f1f5f9', color: '#64748b' },
  success: { bg: '#dcfce7', color: '#15803d' },
  warning: { bg: '#fef3c7', color: '#b45309' },
  danger: { bg: '#fee2e2', color: '#dc2626' },
  info: { bg: '#dbeafe', color: '#1d4ed8' },
  primary: { bg: '#dbeafe', color: 'var(--primary)' },
  before_due: { bg: '#dcfce7', color: '#15803d' },
  on_due: { bg: '#dbeafe', color: '#1d4ed8' },
  after_due: { bg: '#fee2e2', color: '#dc2626' },
  employee: { bg: '#f1f5f9', color: '#475569' },
  supervisor: { bg: '#ede9fe', color: '#7c3aed' },
  manager: { bg: '#dbeafe', color: '#1d4ed8' },
  admin: { bg: '#fce7f3', color: '#be185d' },
};

export function Badge({ label, type = 'info', style = {} }) {
  const c = BADGE_COLORS[type] || BADGE_COLORS.info;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.color, letterSpacing: '.02em', textTransform: 'capitalize',
      ...style
    }}>{label}</span>
  );
}

export function StatusBadge({ isActive }) {
  return <Badge label={isActive ? 'Active' : 'Inactive'} type={isActive ? 'active' : 'inactive'} />;
}

export function FrequencyBadge({ frequency }) {
  return <Badge label={frequency} type="info" />;
}

export function RoleBadge({ role }) {
  return <Badge label={role} type={role} />;
}

export function TimingBadge({ timing }) {
  const labels = { before_due: 'Early', on_due: 'On Time', after_due: 'Late' };
  return <Badge label={labels[timing] || timing} type={timing} />;
}

// ─── Modal ─────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, size = 'md', footer }) {
  if (!open) return null;
  const widths = { sm: 420, md: 560, lg: 720, xl: 900 };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,.5)', backdropFilter: 'blur(4px)' }} />
      <div className="fade-in" style={{
        position: 'relative', width: '100%', maxWidth: widths[size],
        background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 6, display: 'flex' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>{children}</div>
        {footer && <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>{footer}</div>}
      </div>
    </div>
  );
}

// ─── Form components ───────────────────────────────────────────────────────
export function FormField({ label, error, required, children, hint }) {
  return (
    <div style={{ marginBottom: 18 }}>
      {label && (
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, letterSpacing: '.02em' }}>
          {label}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
        </label>
      )}
      {children}
      {hint && !error && <p style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 4 }}>{hint}</p>}
      {error && <p style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={11} />{error}</p>}
    </div>
  );
}

// ─── LoadingSpinner ────────────────────────────────────────────────────────
export function LoadingSpinner({ size = 32 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <div style={{ width: size, height: size, border: `3px solid var(--border)`, borderTop: `3px solid var(--primary)`, borderRadius: '50%' }} className="spin" />
    </div>
  );
}

// ─── EmptyState ────────────────────────────────────────────────────────────
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      {Icon && <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--primary-ghost)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}><Icon size={24} color="var(--primary)" /></div>}
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{title}</div>
      {description && <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>{description}</p>}
      {action}
    </div>
  );
}

// ─── Alert ─────────────────────────────────────────────────────────────────
export function Alert({ type = 'info', message, onClose }) {
  const styles = {
    info: { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', icon: Info },
    success: { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534', icon: CheckCircle },
    warning: { bg: '#fffbeb', border: '#fde68a', color: '#92400e', icon: AlertTriangle },
    danger: { bg: '#fef2f2', border: '#fecaca', color: '#991b1b', icon: AlertTriangle },
  };
  const s = styles[type];
  const Icon = s.icon;
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 'var(--radius-sm)', padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
      <Icon size={16} color={s.color} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, fontSize: 13.5, color: s.color, fontWeight: 500 }}>{message}</div>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: s.color, padding: 0 }}><X size={14} /></button>}
    </div>
  );
}

// ─── Table ─────────────────────────────────────────────────────────────────
export function Table({ columns, data, onRowClick, emptyMessage = 'No data found.' }) {
  if (!data || data.length === 0) {
    return <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>{emptyMessage}</div>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border)' }}>
            {columns.map((col, i) => (
              <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr key={ri} onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{ borderBottom: '1px solid var(--border-light)', transition: 'background .1s', cursor: onRowClick ? 'pointer' : 'default' }}
              onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = 'var(--primary-ghost)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {columns.map((col, ci) => (
                <td key={ci} style={{ padding: '12px 14px', color: 'var(--text-secondary)', verticalAlign: 'middle', ...col.style }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────
export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--border)', marginBottom: 24 }}>
      {tabs.map(tab => (
        <button key={tab.id} onClick={() => onChange(tab.id)} style={{
          padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13.5, fontWeight: 600, color: active === tab.id ? 'var(--primary)' : 'var(--text-muted)',
          borderBottom: active === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
          marginBottom: -2, transition: 'color .15s',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {tab.icon && <tab.icon size={14} />}
          {tab.label}
          {tab.count !== undefined && (
            <span style={{ background: active === tab.id ? 'var(--primary)' : 'var(--border)', color: active === tab.id ? '#fff' : 'var(--text-muted)', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99 }}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── SearchBar ─────────────────────────────────────────────────────────────
export function SearchBar({ value, onChange, placeholder = 'Search…', style = {} }) {
  return (
    <div style={{ position: 'relative', ...style }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ paddingLeft: 36, ...style }} />
    </div>
  );
}
